import { mountReportingSurface } from '../core/reportingSurface.js';
import { loadingState, beginRefresh, settleLoadingValues, failLoadingStates } from '../core/loadingState.js';
import { takePerformancePeriod } from '../core/performanceNavigation.js';
/**
 * analytics-traffic-route.js — client-saas /analytics/traffic
 *
 * GA4 website traffic on the same timeline as magnet leads: sessions, visitors,
 * pageviews, engagement, and the session→lead rate. Reads the owner-scoped
 * GET /m5t/v5/coreAnalyticsMetrics/overlay (server does the day×tenant join).
 * Mirrors ad-performance-route.js — same period buttons, chart kit, provenance
 * pill, and honest empty states (never fabricated numbers).
 */

import AuthController from '../services/AuthController.js';
import { inPortfolio, pickerPageHTML, initPicker } from '../services/portfolio-view.js';
import {
  PROVIDERS, connFor, statusOf, statusPill, fetchIntegrations,
  canManage, ensureStyles as ensureIntgStyles,
} from '../services/integrations-shared.js';
import { statCard } from '../core/statCard.js';
import { ensureChartStyles, dualAxisChartHTML, wireChartHover, fmtAxis } from '../services/chart-kit.js';
import { guardedFetch } from '../core/navGeneration.js';
import { isDenial, denialState, failIfNotOk } from '../core/denialSurface.js';

const apiBase = () => AuthController.getConfig?.().BASE_URL || '';
const ymd = (d) => d.toISOString().slice(0, 10);
const fmtNum = (n) => Number(n || 0).toLocaleString();
const fmtPct = (n) => (n == null ? '—' : `${n}%`);
const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);

let stylesInjected = false;
function ensureStyles() {
  ensureChartStyles();
  ensureIntgStyles();
  if (stylesInjected) return;
  const s = document.createElement('style');
  s.id = 'm5t-traffic-styles';
  s.textContent = `
    .page-shell .traffic-note { font-size: 0.76rem; color: var(--color-text-subtle, #6C7C9A); margin: 12px 2px 0; }
    .page-shell .chart-period-btn { cursor: pointer; }
    .page-shell .chart-period-btn.active { background: var(--color-primary-600, #0072BA); color: #fff; }
    .page-shell .traffic-conn { margin-top: 6px; }
  `;
  document.head.appendChild(s);
  stylesInjected = true;
}

const emptyState = (icon, msg) =>
  `<div class="m5t-empty"><span class="material-symbols-outlined" aria-hidden="true">${icon}</span><p>${msg}</p></div>`;

const PERIODS = [{ d: 7, label: '7 Days' }, { d: 30, label: '30 Days' }, { d: 90, label: '90 Days' }];
let periodDays = 30;

export function render() {
  periodDays = takePerformancePeriod('/analytics/traffic', periodDays);
  if (inPortfolio()) {
    return pickerPageHTML({
      section: 'traffic', title: 'Traffic',
      description: 'Pick a client to view their website traffic.',
    });
  }
  ensureStyles();
  return `
    <div class="page-shell" data-section="traffic">
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Traffic</h1>
          <p class="page-description">Your website traffic alongside the leads your magnets captured.</p>
          <div class="traffic-conn" id="traffic-conn"></div>
        </div>
        <div class="chart-controls" id="traffic-periods">
          ${PERIODS.map((p) => `<button class="chart-period-btn${p.d === periodDays ? ' active' : ''}" type="button" data-days="${p.d}">${p.label}</button>`).join('')}
        </div>
      </div>

      <div class="stats-grid" id="traffic-stats">
        ${statCard({ loading: true, icon: 'ssid_chart', label: 'Sessions', valueId: 'traffic-sessions' })}
        ${statCard({ loading: true, icon: 'group', label: 'Visitors', valueId: 'traffic-users' })}
        ${statCard({ loading: true, icon: 'description', label: 'Page Views', valueId: 'traffic-views' })}
        ${statCard({ loading: true, icon: 'touch_app', label: 'Engagement Rate', valueId: 'traffic-engagement' })}
        ${statCard({ loading: true, icon: 'person_add', label: 'Magnet Leads', valueId: 'traffic-leads', href: '#/leads' })}
        ${statCard({ loading: true, icon: 'percent', label: 'Session to Lead Rate', valueId: 'traffic-leadrate' })}
      </div>

      <div class="dashboard-card">
        <div class="card-header">
          <h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">insights</span> Sessions vs Leads</h2>
          <span class="live-indicator" id="traffic-range"></span>
        </div>
        <div class="card-body" id="traffic-body">
          ${loadingState('chart', 'Loading website traffic')}
        </div>
      </div>
      <p class="traffic-note">Traffic comes from your connected GA4 property, refreshed daily. Leads are magnet captures on the same timeline. Sessions and leads are counted by different systems, so treat the rate as a trend, not an exact funnel.</p>
    </div>`;
}

export async function init() {
  if (inPortfolio()) {
    const fmtN = (n) => Number(n || 0).toLocaleString();
    return initPicker(apiBase(), [
      { h: 'Leads', td: (r) => fmtN(r.leads.window ?? r.leads.total) },
    ], { emptyMsg: 'No client orgs yet. Connect a client GA4 property to see traffic here.' });
  }
  mountReportingSurface('traffic');
  const periods = document.getElementById('traffic-periods');
  periods?.addEventListener('click', (e) => {
    const btn = e.target.closest('button[data-days]');
    if (!btn) return;
    periodDays = Number(btn.dataset.days) || 30;
    periods.querySelectorAll('.chart-period-btn').forEach((b) => b.classList.toggle('active', Number(b.dataset.days) === periodDays));
    load();
  });
  await load();
}

async function load() {
  const loadingTarget = document.getElementById('traffic-body');
  const finishRefresh = beginRefresh(loadingTarget, 'Updating website traffic');
  const base = apiBase();
  const body = document.getElementById('traffic-body');
  const rangeEl = document.getElementById('traffic-range');
  const now = new Date();
  const to = ymd(now);
  const from = ymd(new Date(now.getTime() - (periodDays - 1) * 86400000));
  if (rangeEl) rangeEl.textContent = `${from} → ${to}`;

  let overlay, intg = null;
  try {
    const [oRes, intgRes] = await Promise.all([
      guardedFetch(`${base}/m5t/v5/coreAnalyticsMetrics/overlay?from=${from}&to=${to}`),
      fetchIntegrations().catch(() => null), // notation only — never blocks the page
    ]);
    failIfNotOk(oRes);
    overlay = await oRes.json();
    intg = intgRes;
  } catch (err) {
    failLoadingStates(loadingTarget?.closest('.page-shell'));
    console.warn('[traffic] load failed:', err);
    if (body) body.innerHTML = isDenial(err) ? denialState() : emptyState('error', 'Could not load traffic. Please try again.');
    return;
  } finally {
    finishRefresh();
    settleLoadingValues(loadingTarget?.closest('.page-shell'));
  }

  renderConnNote(intg);

  const t = overlay.totals || {};
  setText('traffic-sessions', fmtNum(t.sessions));
  setText('traffic-users', fmtNum(t.users));
  setText('traffic-views', fmtNum(t.page_views));
  setText('traffic-engagement', fmtPct(t.engagement_rate));
  setText('traffic-leads', fmtNum(t.magnet_leads));
  setText('traffic-leadrate', fmtPct(t.lead_rate));

  if (!overlay.connected) {
    const conn = intg && !intg.denied ? connFor(intg.providers, 'google_analytics') : null;
    body.innerHTML = conn
      ? emptyState('cloud_sync', 'GA4 connected. No traffic for this range yet. Metrics appear here once they sync.')
      : emptyState('link_off', 'No GA4 property connected yet. Connect Google Analytics under Settings, Integrations to see traffic here.');
    return;
  }

  const rows = overlay.rows || [];
  body.innerHTML = svgTrafficChart(rows);
  wireTrafficChart(rows);
}

/** Header notation: connection state, same derivation as Settings → Integrations. */
function renderConnNote(intg) {
  const host = document.getElementById('traffic-conn');
  if (!host) return;
  if (!intg || intg.denied) { host.innerHTML = ''; return; }
  const p = PROVIDERS.google_analytics;
  const conn = connFor(intg.providers, 'google_analytics');
  if (!conn) { host.innerHTML = ''; return; }
  const status = statusOf(p, conn, !!intg.configured?.google_analytics);
  if (status !== 'connected' && status !== 'broken') { host.innerHTML = ''; return; }
  const manage = canManage() ? ` <a href="#/settings/integrations/${p.urlSlug}">Manage</a>` : '';
  host.innerHTML = `<span>Google Analytics</span> ${statusPill(status, conn)}${manage}`;
}

function setText(id, v) { const el = document.getElementById(id); if (el) el.textContent = v; }

// Sessions (bars, left axis) vs magnet leads (line, right axis) via the kit —
// tokens, hover, keyboard, crosshair all shared with the dashboard's charts.
function svgTrafficChart(rows) {
  return dualAxisChartHTML({
    rows,
    xLabel: (r) => String(r.date || '').slice(5),
    left: { value: (r) => r.sessions || 0, label: 'Sessions', fmt: (v) => fmtAxis(v) },
    right: { value: (r) => r.magnet_leads || 0, label: 'Magnet Leads' },
    ariaLabel: 'Website sessions versus magnet leads over time',
    pointLabel: (r) => `${r.date}: ${fmtNum(r.sessions || 0)} sessions, ${fmtNum(r.magnet_leads || 0)} leads`,
    hitData: (r) => ({ day: r.date }),
  });
}

function wireTrafficChart(rows) {
  const host = document.getElementById('traffic-body');
  if (!host) return () => {};
  return wireChartHover(host, {
    rows,
    tipHTML: (r) => `
      <div class="m5c-tip__title">${esc(String(r.date || ''))}</div>
      <div class="m5c-tip__row"><i class="m5c-sw m5c-sw--1"></i>${fmtNum(r.sessions || 0)} sessions</div>
      <div class="m5c-tip__row"><i class="m5c-sw m5c-sw--2"></i>${fmtNum(r.magnet_leads || 0)} leads</div>`,
  });
}
